import BaseModel from "./BaseModel";
import BaseModelMap from "./BaseModelMap";
import type Consumer from "./Consumer";
import type Terminal from "./Terminal";
import type { CableType } from "./CableType";
import type { VirtualConnection } from "../connectionAnalyser/VirtualConnections";

export type WireDestination = { consumer: Consumer; wireIndex: number; name: string; color?: string };

/** A derived conductor junction; it is neither hardware nor a persisted project object. */
export default class WireEndpoint extends BaseModel {
  constructor(owner?: Consumer, index = 0) {
    super({ storeInObjectRegistry: false, isPersistent: false }, owner);
    this.index = index;
    this._id = `wire-endpoint:${owner?.id ?? ""}:${index}`;
    this._tag = String(index + 1);
  }

  readonly index: number;
  color?: string;
  terminal?: Terminal;
  outgoing: WireDestination[] = [];
  connections: readonly VirtualConnection[] = [];

  get className(): string { return "WireEndpoint"; }
  canBeDeletedInteractive(): boolean { return false; }
  canBeCopiedInteractive(): boolean { return false; }
  canBeEditedInteractive(): boolean { return false; }
}

/** Reads the user's conductor names and colors before the cable defaults. */
function wireAppearance(cable: CableType | undefined, index: number): { name: string; color?: string } {
  return {
    name: cable?.wireNames[index] || cable?.wireDefinitions[index]?.longName || `Ader ${index + 1}`,
    color: cable?.htmlColors[index] || cable?.wireDefinitions[index]?.htmlColor,
  };
}

/** Reconciles transient objects without emitting project changes or replacing surviving identities. */
export function calculateWireEndpoints(owner: Consumer): BaseModelMap<WireEndpoint> | undefined {
  const destinations = owner.getIncommingTerminalViaConsumers().filter(consumer => !consumer.isDeleted);
  const previous = owner.wireEndpoints;
  if (owner.isDeleted || destinations.length === 0) {
    for (const endpoint of previous?.asArray() ?? []) {
      endpoint.isDeleted = true;
      endpoint.connections = [];
    }
    return undefined;
  }
  const collection = previous ?? new BaseModelMap(WireEndpoint, undefined, undefined, false);
  collection.parent = owner;
  collection.propertyId = "wireEndpoints";
  const oldEndpoints = new Map<number, WireEndpoint>();
  for (const endpoint of collection.asArray()) oldEndpoints.set(endpoint.index, endpoint);
  collection.items.clear();
  const cable = owner.cableType;
  const block = owner.terminal?.getTerminalBlock();
  for (let index = 0; index < (cable?.numberOfWires ?? 0); index++) {
    const endpoint = oldEndpoints.get(index) ?? new WireEndpoint(owner, index);
    oldEndpoints.delete(index);
    const appearance = wireAppearance(cable, index);
    endpoint._name = appearance.name;
    endpoint.color = appearance.color;
    endpoint.outgoing = [];
    endpoint.connections = [];
    endpoint.terminal = block?.terminalFromIndex(block.indexOfTerminalfromWireIndex(index));
    collection.addWithoutUpdate(endpoint);
  }
  for (const endpoint of oldEndpoints.values()) {
    endpoint.isDeleted = true;
    endpoint.connections = [];
  }
  for (const consumer of destinations) {
    const start = consumer.terminalViaWireIndex;
    if (start === undefined || !Number.isInteger(start) || start < 0) continue;
    const count = consumer.wireRequirement.numberOfLWiresWithoutPEandN || 1;
    const outgoingStart = consumer.cableType?.hasGround === false ? 1 : 2;
    for (let offset = 0; offset < count; offset++) {
      const endpoint = collection.at(start + offset);
      if (!endpoint) continue;
      const wireIndex = outgoingStart + offset;
      endpoint.outgoing.push({ consumer, wireIndex, ...wireAppearance(consumer.cableType, wireIndex) });
    }
  }
  return collection;
}
